import { defineStore } from 'pinia';
import axios from 'axios';
export const useExpenseSettingStore = defineStore('expenseSettingStore', {
  state: () => ({
    saving: false,
    loading: false,
    alerts: {},
    me: {},
    company: {},
    stepTitle: null,
    data: {
      step: 0,
      incomeAccount: {
        account: null,
        create: false,
        name: 'Legal Services'
      },
      incomeService: {
        service: null,
        create: false,
        name: ''
      },
      sync: null,
      syncDate: null,
      onlyBillable: false,
      hardAccount: {
        account: null,
        create: false,
        name: 'Prepaid Expenses'
      },
      hardService: {
        service: null,
        create: false,
        name: ''
      },
      same: false,
      softAccount: {
        account: null,
        create: false,
        name: 'Billable Expenses'
      },
      softService: {
        service: null,
        create: false,
        name: ''
      },
      filteredIncome: [],
      filteredHard: [],
      filteredSoft: []
    },
    step: {
      instructions: 0,
      incomeAccount: 1,
      incomeService: 2,
      sync: 3,
      hardAccount: 4,
      hardService: 5,
      syncDate: 6,
      same: 7,
      softAccount: 8,
      softService: 9
      // Add more steps if necessary
    },
    services: [],
    hardAccounts: [],
    softAccounts: [],
    incomeAccounts: [],
    initialSetup: false
  }),
  actions: {
    async loadInitialData(userService, accountingService) {
      this.loading = true;
      try {
        const [me, services, accounts] = await Promise.all([userService.getMe(), accountingService.services(), accountingService.accounts()]);
        this.me = me;
        this.company = me.company;
        this.services = services.services;
        this.hardAccounts = accountingService.combineQuickBooksAccounts(accounts, ['asset', 'expense', 'liability', 'income']).filter(a => a.subtype !== 'TrustAccounts' && a.subtype !== 'TrustAccountsLiabilities');
        this.softAccounts = accountingService.combineQuickBooksAccounts(accounts, ['income', 'expense', 'asset', 'liability']);
        this.incomeAccounts = accounts.income;
        const settings = me.company.settings.expenses || {};
        this.initialSetup = !settings.accountingService;
        this.data.sync = settings.sync;

        // Load income service from top-level company settings
        if (me.company.settings.accountingService) {
          this.data.incomeService.service = me.company.settings.accountingService;
          this.data.incomeAccount.account = me.company.settings.accountingService.account;
        }
        if (settings.syncAccounts && settings.syncAccounts.length) {
          this.data.hardAccount.account = settings.syncAccounts[0].account;
          this.data.hardService.service = settings.syncAccounts[0].service;
        }
        this.data.syncDate = settings.syncDate;

        // Load the LeanLaw expense service (soft service) from expenses.accountingService
        if (settings?.accountingService) {
          this.data.softService.service = settings.accountingService;
          this.data.softAccount.account = settings.accountingService?.account;
        }
        this.stepTitle = this.getCurrentTitleFromStep(this.data.step);
      } catch (error) {
        this.alerts?.error('Failed to load initial data:');
      } finally {
        this.loading = false;
      }
    },
    moveToNextStep() {
      const currentStep = this.data.step;
      if (currentStep === this.step.incomeAccount) {
        this.data.filteredIncome = this.prepareService(this.data.incomeAccount, this.data.incomeService);
      } else if (currentStep === this.step.hardAccount) {
        this.data.filteredHard = this.prepareService(this.data.hardAccount, this.data.hardService);
      } else if (currentStep === this.step.softAccount) {
        this.data.filteredSoft = this.prepareService(this.data.softAccount, this.data.softService);
      }
      this.data.step++;
      if (this.data.step === this.step.hardAccount && !this.data.sync) {
        const skip = this.step.softAccount - this.step.hardAccount;
        this.data.step += skip;
        this.data.stepOffset += skip;
      }
      if (this.data.step === this.step.syncDate && this.data.hardAccount.create) {
        this.data.stepOffset += 1;
        this.data.step += 1;
      }
      this.stepTitle = this.getCurrentTitleFromStep(this.data.step);
    },
    prepareService(account, service) {
      const filtered = account.create ? [] : this.services.filter(s => s.account?.id === account.account?.id);
      if (service.service) {
        service.service = {
          ...filtered.find(s => s.id === service.service?.id)
        };
      }
      if (!filtered.length) {
        service.create = true;
        service.name = account.create ? account.name : account.account?.name;
      } else if (!service.service) {
        service.service = {
          ...filtered[0]
        };
      }
      return filtered;
    },
    getCurrentTitleFromStep(currentStep) {
      const step = Number(this.data.step) || 0;
      const stepOffset = Number(this.data.stepOffset) || 0;
      const stepNumber = step - stepOffset;
      let stepPrefix = '';
      if (currentStep > this.step.instructions) {
        stepPrefix = `Step ${stepNumber} - `;
      }
      switch (currentStep) {
        case this.step.instructions:
          return stepPrefix + 'QuickBooks Account Setup';
        case this.step.incomeAccount:
          return stepPrefix + 'Services: Account';
        case this.step.incomeService:
          return stepPrefix + 'Services: Product/Service';
        case this.step.sync:
          return stepPrefix + 'Sync Expenses';
        case this.step.hardAccount:
          return stepPrefix + 'QuickBooks Expenses: Account';
        case this.step.hardService:
          return stepPrefix + 'QuickBooks Expenses: Product/Service';
        case this.step.syncDate:
          return stepPrefix + 'Date Range';
        case this.step.same:
          return stepPrefix + 'LeanLaw Expenses';
        case this.step.softAccount:
          return stepPrefix + 'LeanLaw Expenses: Account';
        case this.step.softService:
          return stepPrefix + 'LeanLaw Expenses: Product/Service';
        default:
          return;
      }
    },
    async createOrSelectAccountAndService(account, service, type, subtype, accountingService, alerts) {
      try {
        const createdAccount = await this.createOrSelectAccount(account, type, subtype, accountingService, alerts);
        account.account = createdAccount;
        const createdService = await this.createOrSelectService(service, createdAccount, accountingService, alerts);
        service.service = createdService;
        return {
          account: createdAccount,
          service: createdService
        };
      } catch (error) {
        throw error;
      }
    },
    async createOrSelectAccount(account, type, subtype, accountingService, alerts) {
      // Check if account exists AND has an id (meaning it's a real QuickBooks account)
      if (account.account && account.account.id) {
        return Promise.resolve(account.account);
      }
      if (account.create && account.name) {
        try {
          const newAccount = await accountingService.createAccount({
            name: account.name,
            type,
            subtype
          });
          return newAccount;
        } catch (error) {
          const errorMessage = `Failed to create account "${account.name}": ${error.message || error}`;
          alerts.error(errorMessage);
          throw error;
        }
      }
      const errorMessage = 'Must either select or create an account';
      alerts.error(errorMessage);
      return Promise.reject(errorMessage);
    },
    async createOrSelectService(service, account, accountingService, alerts) {
      if (service.service) {
        return Promise.resolve(service.service);
      }
      if (service.create && service.name) {
        if (!account || Object.keys(account).length === 0) {
          const errorMessage = `Cannot create service "${service.name}" - account is missing or empty`;
          alerts.error(errorMessage);
          return Promise.reject(errorMessage);
        }
        try {
          const newService = await accountingService.createService({
            name: service.name,
            account: account
          });
          return newService;
        } catch (error) {
          const errorMessage = `Failed to create service "${service.name}": ${error.message || error}`;
          alerts.error(errorMessage);
          throw error;
        }
      }
      const errorMessage = 'Must either select or create a product/service';
      alerts.error(errorMessage);
      return Promise.reject(errorMessage);
    },
    showSave() {
      if (this.data.step === this.step.same) {
        return !!this.data.same;
      }
      return this.data.step === this.step.softService;
    }
  },
  getters: {
    enableNext: state => {
      const serviceExists = (serviceName, services) => {
        return services?.some(service => service.name === serviceName);
      };
      switch (state.data.step) {
        case state.step.instructions:
          return true;
        case state.step.incomeAccount:
          return !!state.data.incomeAccount.account || !!(state.data.incomeAccount.create && state.data.incomeAccount.name);
        case state.step.incomeService:
          return !!state.data.incomeService.service || state.data.incomeService.create && state.data.incomeService.name && state.data.incomeService.name.trim() !== '' && !serviceExists(state.data.incomeService.name, state.services);
        case state.step.sync:
          return state.data.sync !== null;
        case state.step.syncDate:
          return state.data.syncDate !== null;
        case state.step.same:
          return state.data.same !== null;
        case state.step.hardAccount:
          return !!state.data.hardAccount.account || !!(state.data.hardAccount.create && state.data.hardAccount.name);
        case state.step.hardService:
          return !!state.data.hardService.service || state.data.hardService.create && state.data.hardService.name && state.data.hardService.name.trim() !== '' && !serviceExists(state.data.hardService.name, state.services);
        case state.step.softAccount:
          return !!state.data.softAccount.account || !!(state.data.softAccount.create && state.data.softAccount.name);
        case state.step.softService:
          return !!state.data.softService.service || state.data.softService.create && state.data.softService.name && state.data.softService.name.trim() !== '' && !serviceExists(state.data.softService.name, state.services);
        default:
          return false;
      }
    }
  }
});